import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import { Navigate, RouterProvider, createHashRouter } from 'react-router-dom';

import Layout from './Layout';
import RequireAuth from './components/RequireAuth';
import AdminPanel, {
	AddCBO,
	CredentialedCBOs,
	EligibleEnrollees,
	ModifyCBO,
} from './pages/AdminPanel';
import CompletedILOS from './pages/AdminPanel/components/CompletedILOS';
import ApplicationModules, {
	PrintReferralDetail,
} from './pages/ApplicationModules';
import Dashboard from './pages/Dashboard';
import ErrorPage from './pages/Error';
import ForgotPassword, { forgotPasswordAction } from './pages/ForgotPassword';
import Funds, { FundsForm } from './pages/Funds';
import ILOSPanel, {
	CredentialedServiceList,
	Invoicing,
} from './pages/ILOSPanel';
import CompletedReportExport from './pages/ILOSPanel/components/CompletedReportExport';
import Login, { loginAction } from './pages/Login';
import Logout from './pages/Logout';
import MyWorklist from './pages/MyWorklist';
import NewApplication from './pages/NewApplication';
import AvailableOrganizations from './pages/NewApplication/components/AvailableOrganizations';
import ChooseClient from './pages/NewApplication/components/ChooseClient';
import ConfirmChoices from './pages/NewApplication/components/ConfirmChoices';
import SecureAccount from './pages/NewApplication/components/SecureAccount';
import ZipCodeSearch from './pages/NewApplication/components/ZipCodeSearch';
import Preferences from './pages/Preferences';
import Reporting, { ReportingDashboard, Reports } from './pages/Reporting';
import ResetPassword, {
	resetPasswordAction,
	resetPasswordLoader,
} from './pages/ResetPassword';
import Services, { ServicesConfiguration } from './pages/Services';

axios.defaults.baseURL = process.env.BASE_URL;
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false, // default: true
			retry: false, // default: 3
		},
	},
});
const router = createHashRouter([
	{
		element: (
			<RequireAuth>
				<Layout />
			</RequireAuth>
		),
		children: [
			{
				element: <MyWorklist />,
				path: '/worklist/:referralType/:page?',
				errorElement: <ErrorPage />,
			},
			{
				element: <NewApplication />,
				path: 'newApplication',
				errorElement: <ErrorPage />,
				children: [
					{ index: true, element: <ZipCodeSearch />, path: '*' },
					{ path: 'profile/:type?', element: <ChooseClient /> },
					{ path: 'organizations', element: <AvailableOrganizations /> },
					{ path: 'secure', element: <SecureAccount /> },
					{ path: 'confirm', element: <ConfirmChoices /> },
					{ path: 'search', element: <ZipCodeSearch /> },
				],
			},
			{
				element: <AdminPanel />,
				path: '/admin-panel',
				errorElement: <ErrorPage />,
			},
			{
				element: <CredentialedCBOs />,
				path: '/admin-panel/cred-cbos',
				errorElement: <ErrorPage />,
			},
			{
				element: <AddCBO />,
				path: '/admin-panel/cred-cbos/add',
				errorElement: <ErrorPage />,
			},
			{
				element: <ModifyCBO />,
				path: '/admin-panel/cred-cbos/modify/:agencyLinkId',
				errorElement: <ErrorPage />,
			},
			{
				element: <EligibleEnrollees />,
				path: '/admin-panel/elig-enroll',
				errorElement: <ErrorPage />,
			},
			{
				element: <CompletedILOS />,
				path: '/admin-panel/ilos-completed',
				errorElement: <ErrorPage />,
			},
			{
				element: <ILOSPanel />,
				path: '/ilos-panel',
				errorElement: <ErrorPage />,
			},
			{
				element: <CredentialedServiceList />,
				path: '/ilos-panel/credentialed-services',
				errorElement: <ErrorPage />,
			},
			{
				element: <Invoicing />,
				path: '/ilos-panel/invoicing',
				errorElement: <ErrorPage />,
			},
			{
				element: <CompletedReportExport />,
				path: '/ilos-panel/export',
				errorElement: <ErrorPage />,
			},
			{
				element: <Services />,
				path: '/services',
				errorElement: <ErrorPage />,
			},
			{
				element: <ServicesConfiguration />,
				path: '/services/:serviceId/configure',
				errorElement: <ErrorPage />,
			},
			{
				element: <Funds />,
				path: '/funds',
				errorElement: <ErrorPage />,
			},
			{
				element: <FundsForm />,
				path: '/funds/add',
				errorElement: <ErrorPage />,
			},
			{
				element: <FundsForm />,
				path: '/funds/:fundId',
				errorElement: <ErrorPage />,
			},
			{
				element: <Reporting />,
				path: '/reporting',
				errorElement: <ErrorPage />,
			},
			{
				element: <Reports />,
				path: '/reporting/reports',
				errorElement: <ErrorPage />,
			},
			{
				element: <ReportingDashboard />,
				path: '/reporting/dashboard',
				errorElement: <ErrorPage />,
			},
			{
				element: <Dashboard />,
				path: '/:dashType/:page?',
				errorElement: <ErrorPage />,
			},
			{
				element: <Dashboard />,
				path: '/:page?',
				errorElement: <ErrorPage />,
			},
		],
	},
	{
		element: (
			<RequireAuth>
				<PrintReferralDetail />
			</RequireAuth>
		),
		path: '/worklist/application/:requestId/print',
		errorElement: <ErrorPage />,
	},
	{
		element: (
			<RequireAuth>
				<Layout hideNav />
			</RequireAuth>
		),
		children: [
			{
				element: <ApplicationModules />,
				path: '/worklist/application/:requestId',
				errorElement: <ErrorPage />,
			},
			{
				element: <Preferences />,
				path: '/preferences',
				errorElement: <ErrorPage />,
			},
		],
	},
	{ element: <Login />, path: '/login', action: loginAction },
	{
		element: <ForgotPassword />,
		path: '/forgotPassword',
		action: forgotPasswordAction,
	},
	{
		element: <ResetPassword />,
		path: '/reset',
		action: resetPasswordAction,
		loader: resetPasswordLoader,
	},
	{ element: <Logout />, path: '/logout' },
	{
		element: <Navigate to="/" />,
		path: '*',
	},
]);
// Add a response interceptor
axios.interceptors.response.use(
	function (response) {
		// Any status code that lie within the range of 2xx cause this function to trigger
		// Do something with response data
		return response;
	},
	function (error) {
		// Any status codes that falls outside the range of 2xx cause this function to trigger
		// Do something with response error
		if (
			error?.config?.headers?.Authorization &&
			401 === error?.response?.status
		) {
			// console.log('LOGOUT from axios')
			// TODO show spinner with logging out messager?
			router.navigate('/login');
		} else {
			return Promise.reject(error);
		}
	},
);

const App = () => (
	<QueryClientProvider client={queryClient}>
		<RouterProvider router={router} />
	</QueryClientProvider>
);

export default App;
